import type { ReactElement } from 'react';
import { EmberMascot } from './EmberMascot';
import './PageLoading.css';

export interface PageLoadingProps {
  /** The wait copy, announced politely. Keep it short and promise nothing. */
  label?: string;
  /**
   * `page` owns the viewport below the nav — a route with no renderable data
   * yet. `inline` sits in a section or list slot that already has page chrome
   * above it.
   */
  variant?: 'page' | 'inline';
  /** Spacing and grid placement only; the mark and its motion stay shared. */
  className?: string;
}

/**
 * The shared centred wait state for any surface with nothing to render yet
 * (docs/frontend-ui-standard.md §5). Ember itself does the waiting: the mascot
 * thinking — eyes up, a paw at the chin — the same creature as everywhere
 * else, so a wait always looks like Ember
 * rather than like a borrowed spinner. Pinned to `thinking`: a wait mark must
 * not wander into other faces or answer taps.
 */
export function PageLoading({
  label = 'Loading…', variant = 'page', className = '',
}: PageLoadingProps): ReactElement {
  return (
    <div
      className={`ember-loading ember-loading-${variant}${className ? ` ${className}` : ''}`}
      role="status"
      aria-live="polite"
    >
      <span className="ember-loading-mark" aria-hidden>
        <EmberMascot face="thinking" className="ember-loading-mascot" />
      </span>
      <span className="ember-loading-label">{label}</span>
    </div>
  );
}
